import { site } from "../lib/content";
import { getSession, logout } from "../lib/auth";
import { navigate, currentPath } from "../lib/router";

export function renderDisclaimer(): string {
  return `
    <div class="disclaimer-banner" role="note">
      <div class="container">
        <strong>Note</strong>
        <span>${site.disclaimers.banner}</span>
      </div>
    </div>
  `;
}

function navLink(href: string, label: string, path: string): string {
  const current = path === href || (href !== "/" && path.startsWith(href));
  return `<a href="${href}" ${current ? 'aria-current="page"' : ""}>${label}</a>`;
}

export function renderHeader(): string {
  const path = currentPath();
  const session = getSession();
  const links = site.nav.public
    .map((l) => navLink(l.href, l.label, path))
    .join("");

  let authBtns = "";
  if (session) {
    const dest = session.role === "admin" ? "/admin" : "/members";
    authBtns = `
      <a class="btn btn--sm btn--secondary" href="${dest}">${session.role === "admin" ? "Admin" : "Members"}</a>
      <button type="button" class="btn btn--sm btn--secondary" data-logout>Log out</button>
    `;
  } else {
    authBtns = `
      <a class="btn btn--sm btn--secondary" href="/members/login">Member login</a>
      <a class="btn btn--sm btn--primary" href="/join">Join</a>
    `;
  }

  return `
    <header class="site-header">
      <div class="container site-header__inner">
        <a class="brand" href="/" aria-label="${site.club.name} home">
          <img class="brand__logo" src="/brand/logo.svg" alt="" width="220" height="52" />
        </a>
        <nav class="nav-desktop" aria-label="Primary">${links}</nav>
        <div class="nav-actions">
          ${authBtns}
          <button type="button" class="nav-toggle" aria-label="Open menu" aria-expanded="false" aria-controls="nav-mobile" data-nav-toggle>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 7h16M4 12h16M4 17h16"/></svg>
          </button>
        </div>
      </div>
      <div class="nav-mobile container" id="nav-mobile" data-nav-mobile hidden>
        ${links}
        <div class="row" style="margin-top:1rem">${authBtns}</div>
      </div>
    </header>
  `;
}

export function renderFooter(): string {
  return `
    <footer class="site-footer">
      <div class="container">
        <div class="footer-grid">
          <div>
            <div class="footer-brand">${site.club.name}</div>
            <p>${site.club.tagline}</p>
            <p class="pill pill--gold" style="margin-top:0.75rem">${site.disclaimers.short}</p>
          </div>
          <div>
            <h4>Explore</h4>
            <ul class="footer-links">
              <li><a href="/about">How it works</a></li>
              <li><a href="/horse">The horse</a></li>
              <li><a href="/membership">Shares</a></li>
              <li><a href="/faq">FAQ</a></li>
              <li><a href="/join">Join</a></li>
            </ul>
          </div>
          <div>
            <h4>Account & legal</h4>
            <ul class="footer-links">
              <li><a href="/members/login">Member login</a></li>
              <li><a href="/admin/login">Admin login</a></li>
              <li><a href="/privacy">Privacy <span class="pill" style="font-size:0.7rem">Draft — not final</span></a></li>
              <li><a href="/terms">Syndicate Terms <span class="pill" style="font-size:0.7rem">Draft — not final</span></a></li>
              <li><a href="/complaints">Complaints <span class="pill" style="font-size:0.7rem">Draft — not final</span></a></li>
              <li><a href="/contact">Contact</a></li>
            </ul>
          </div>
        </div>
        <div class="footer-meta">
          © ${new Date().getFullYear()} ${site.club.name}. Demo site — no live payments or email. Syndicate share product — prize money annually by %; returns not guaranteed.
        </div>
      </div>
    </footer>
  `;
}

export function mountChrome(): void {
  const root = document.getElementById("app");
  if (!root) return;
  if (!root.querySelector("[data-shell]")) {
    root.innerHTML = `
      <div class="app-shell" data-shell>
        <a class="skip-link" href="#app-main">Skip to content</a>
        <div data-disclaimer></div>
        <div data-header></div>
        <main id="app-main" tabindex="-1"></main>
        <div data-footer></div>
      </div>
    `;
  }
  root.querySelector("[data-disclaimer]")!.innerHTML = renderDisclaimer();
  root.querySelector("[data-header]")!.innerHTML = renderHeader();
  root.querySelector("[data-footer]")!.innerHTML = renderFooter();

  const toggle = root.querySelector("[data-nav-toggle]") as HTMLButtonElement | null;
  const mobile = root.querySelector("[data-nav-mobile]") as HTMLElement | null;
  toggle?.addEventListener("click", () => {
    if (!mobile || !toggle) return;
    const open = !mobile.classList.contains("is-open");
    mobile.classList.toggle("is-open", open);
    mobile.hidden = !open;
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
    toggle.setAttribute("aria-label", open ? "Close menu" : "Open menu");
  });

  root.querySelectorAll("[data-logout]").forEach((btn) => {
    btn.addEventListener("click", () => {
      logout();
      navigate("/");
      mountChrome();
    });
  });
}

export function setMain(html: string): void {
  mountChrome();
  const main = document.getElementById("app-main");
  if (main) main.innerHTML = html;
}

export function portalNav(
  items: Array<{ href: string; label: string }>,
  path: string
): string {
  return `
    <nav class="portal-nav" aria-label="Portal">
      ${items
        .map((i) => {
          const cur = path === i.href;
          return `<a href="${i.href}" ${cur ? 'aria-current="page"' : ""}>${i.label}</a>`;
        })
        .join("")}
    </nav>
  `;
}
